import {
  SECTION_DEFAULT_FILL,
  TABLE_BORDER_PX,
  TABLE_CELL_PADDING_EM,
  cellInlineStylesAt,
  compareCanvasObjectsForRender,
  isDrawableObject,
  resolveTableObjectColumnWidths,
  resolveTableObjectRowHeights,
  type ArrowCorner,
  type ArrowEdge,
  type CanvasFrame,
  type CanvasObject,
  type EmojiStampKind,
  type TableColumnWidthsList,
  type TextInlineStyleRange,
} from "@refinement-canvas/domain";
import { CANVAS_GRID_PX } from "./canvas-coordinates";
import { resolveArrowEdgePath, resolveArrowPath } from "./shape-arrow";
import { SHAPE_DEFAULT_STROKE, SHAPE_DEFAULT_STROKE_WIDTH } from "./shape-preference";
import { CONNECTOR_DEFAULT_STROKE, CONNECTOR_DEFAULT_STROKE_WIDTH } from "./connector-style";
import { resolveRectCornerRadius, traceRoundedRectPath } from "./shape-rect";
import {
  STICKY_PADDING_PX,
  computeStickyBodyReservePx,
  computeStickySignatureFontPx,
} from "./sticky-font-fit";
import { arrowHeadPoints, computeConnectorRoute } from "./connector-geometry";
import { layoutInlineSpansForCanvas, layoutMarkdownTextForCanvas } from "./markdown-canvas-layout";
import {
  CALLOUT_COLORS,
  MARKDOWN_CODE_BACKGROUND_COLOR,
  MARKDOWN_CODE_BORDER_COLOR,
  MARKDOWN_CODE_BORDER_PX,
  MARKDOWN_RULE_COLOR,
  MARKDOWN_TABLE_BORDER_COLOR,
  MARKDOWN_TABLE_BODY_BACKGROUND_COLOR,
  MARKDOWN_TABLE_HEADER_BACKGROUND_COLOR,
  MARKDOWN_TABLE_HEADER_TEXT_COLOR,
  MARKDOWN_TABLE_ROW_STRIPE_COLOR,
  TASK_BOX_CHECK_COLOR,
  TASK_BOX_CHECKED_COLOR,
  tableObjectCellDisplayText,
  tableObjectCellSpans,
} from "./markdown-text";
import { preloadImages, type LinkCardImageResolver } from "./canvas-raster-images";
import {
  canvasFontFor,
  computeStickyExportFontPx,
  createCanvasMeasure,
  resolveLayoutAlign,
} from "./canvas-sticky-text-metrics";
import { resolveStickyTextPalette } from "./sticky-text-color";
import { isSnapshotAvatarImageUrl } from "../auth/avatar-image-url";
import { resolveTextBackground, TEXT_DEFAULT_COLOR } from "./text-color";
import {
  applyCanvasStrokePattern,
  fillDottedStrokeStartFallback,
  resolveStrokePattern,
} from "./stroke-pattern";
import { displayTextOfLinkifiedText } from "./linkify-text";
import {
  prefersLinkColorOnDark,
  resolveTableBodyPalette,
  type MarkdownTableBodyPalette,
} from "./text-link-color";

// PDF 出力用に、キャンバス全体を高解像度のラスタ（PNG または JPEG）として描画する。
// 印刷経路は、別ウィンドウで PNG を表示できない場合も解像度ラダーの次段へ降格して再生成する。
// 既存サムネイル描画（renderCanvasThumbnailDataUrl）の高解像度版で、画像 / 動画オブジェクトについては
// 利用可能であれば画像本体を読み込んで貼り付ける（読み込み失敗時は塗りつぶしのプレースホルダで代替）。
//
// 戻り値: data URL（要求した形式に一致するもの。既定は image/png）。失敗時は空文字列。

// 全体像 PDF の基準となる描画幅（A4 横 300DPI 相当。約 3508x1973、16:9 で描画する）。
// この定数は次の 4 つの役割を兼ねるため、値の変更は 4 つすべてへ同時に効くことに注意する。
// 1. renderCanvasExportImage の既定描画幅（自動保存スナップショット以外の既定）
// 2. drawGrid のグリッド密度の基準幅（値を変えるとキャンバス座標換算のグリッド密度が変わる）
// 3. 手動出力の解像度ラダーの最終段（現行品質の下限保証。buildManualExportWidthLadder の末尾）
// 4. ダウンロード経路の埋め込み形式のしきい値（この幅を超える段は JPEG、以下の段は PNG）
export const EXPORT_WIDTH_PX = 3508;
// 自動縮小の下限幅。最低限の可読性を保つため、これ以上は縮小しない。
export const MIN_EXPORT_WIDTH_PX = 1000;

/** 手動 PDF 出力のラスタ形式。 */
export type ExportRasterFormat = "png" | "jpeg";

/** JPEG 埋め込み時のエンコード品質（0〜1）。文字の可読性を保つため高めに固定する。 */
export const EXPORT_JPEG_QUALITY = 0.92;

/**
 * 手動出力の最大描画幅。PDF のページ寸法上限（14400 ポイント = 200 インチ、ISO 32000-1 附属書 C）に合わせる。
 * 本実装はページ寸法を埋め込みラスタの画素数から作るため、この幅を超えると不正なページ寸法の PDF になる。
 * 現行 3 プリセットの最大幅は 12288 のため実際には働かないが、将来プリセットが拡張されたときに
 * 黙って不正な PDF を作らないための固定ガードとして置く。
 */
export const MANUAL_EXPORT_MAX_WIDTH_PX = 14400;

/** 解像度ラダーの縮小係数（幅 0.8 倍 ＝ 面積約 0.64 倍ごとに刻む）。 */
export const MANUAL_EXPORT_LADDER_FACTOR = 0.8;

/** 許可する画像 data URL のヘッダ（要求形式との一致検証と、印刷ウィンドウへの埋め込み検証に使う）。 */
const DATA_URL_PREFIX: Readonly<Record<ExportRasterFormat, string>> = {
  png: "data:image/png;base64,",
  jpeg: "data:image/jpeg;base64,",
};

/**
 * data URL の本体として受け付ける形（標準 Base64 のみ、1 文字以上）。
 * 印刷用 HTML では data URL を `<img src="…">` の属性値へ埋めるため、prefix の一致だけで通すと
 * `"` を含む値が属性境界を破って別の属性（onerror 等）を注入できてしまう。現在の呼び出し元は
 * `canvas.toDataURL()` の戻り値だけだが、将来別経路の値が渡っても壊れないよう本体側で狭める。
 */
const DATA_URL_BASE64_BODY = /^[A-Za-z0-9+/]+={0,2}$/;

/**
 * 印刷ウィンドウ側で画像のデコードに失敗したときに表示する案内文（決定論的なダウンロード経路へ誘導する）。
 * この文字列は印刷用 HTML の onerror 属性内の JS 文字列リテラルへそのまま埋め込むため、
 * 引用符（' "）、バックスラッシュ、改行、`<` を含めてはいけない（HTML と JS の両方の構文が壊れる）。
 */
const PRINT_WINDOW_DECODE_ERROR_MESSAGE =
  "画像の表示に失敗しました。アプリメニューの PDF ダウンロードをお試しください。";

/** 印刷ウィンドウが開き元へ返す画像表示結果のメッセージ種別。 */
export const PDF_EXPORT_IMAGE_RESULT_MESSAGE_TYPE = "refinement-canvas:pdf-export-image-result";

/** 印刷ウィンドウからの画像表示結果。 */
export type PrintWindowImageResult = "loaded" | "decode-failed";

/** 開き元だけが観測できる終端も含めた画像表示の待ち受け結果。 */
export type PrintWindowImageWait = PrintWindowImageResult | "closed" | "timeout";

/** 印刷ウィンドウからの通知を待つ上限（ms）。 */
export const PRINT_WINDOW_IMAGE_RESULT_TIMEOUT_MS = 120_000;

/** 印刷ウィンドウが閉じられたことを検知する間隔（ms）。 */
export const PRINT_WINDOW_CLOSED_POLL_MS = 500;

/** 印刷用画像の生成中に表示する既定文言。 */
const PRINT_WINDOW_GENERATING_MESSAGE =
  "PDF 出力用の画像を生成しています。しばらくお待ちください。";

/** デコード失敗後、解像度を下げて再生成している間に表示する文言。 */
export const PRINT_WINDOW_RETRY_MESSAGE =
  "画像の表示に失敗したため、解像度を下げて再生成しています。しばらくお待ちください。";

// 時刻や乱数だけに依存せず、同一プロセス内で試行トークンが重複しないよう連番を保持する。
let printImageResultTokenSeq = 0;

/** HTML 属性と JavaScript 文字列へ安全に埋め込める英数字だけの試行トークンを返す。 */
export function createPrintImageResultToken(): string {
  printImageResultTokenSeq += 1;
  return `${Date.now().toString(36)}${printImageResultTokenSeq.toString(36)}`;
}

/** data URL が指定形式の非空な画像 data URL かどうか。 */
function isImageDataUrlOf(dataUrl: string, format: ExportRasterFormat): boolean {
  const prefix = DATA_URL_PREFIX[format];
  return dataUrl.startsWith(prefix) && DATA_URL_BASE64_BODY.test(dataUrl.slice(prefix.length));
}

/**
 * 手動 PDF 出力（印刷 / ダウンロード）で試す描画幅を、解像度の高い順に返す。
 * 先頭はキャンバス論理幅と等倍（MANUAL_EXPORT_MAX_WIDTH_PX で上限）、以降は 0.8 倍ずつ刻み、
 * 末尾は常に EXPORT_WIDTH_PX（現行の既定幅＝現行品質の下限保証）。
 * EXPORT_WIDTH_PX 以下になった候補は置かないため、戻り値は重複の無い厳密な降順になる。
 * キャンバス論理幅が EXPORT_WIDTH_PX 以下の場合は [EXPORT_WIDTH_PX] の 1 段だけになり、現行と同じ挙動になる。
 */
export function buildManualExportWidthLadder(canvasRect: CanvasFrame): readonly number[] {
  const widths: number[] = [];
  let width = Math.round(Math.min(canvasRect.w, MANUAL_EXPORT_MAX_WIDTH_PX));
  // EXPORT_WIDTH_PX 以下へ落ちた時点で打ち切り、末尾に床値を 1 つだけ置く。
  // 係数 0.8 の縮小は EXPORT_WIDTH_PX 超の範囲で必ず 700px 以上減るため、この反復は有限で重複も生じない。
  while (width > EXPORT_WIDTH_PX) {
    widths.push(width);
    width = Math.round(width * MANUAL_EXPORT_LADDER_FACTOR);
  }
  widths.push(EXPORT_WIDTH_PX);
  return widths;
}

/**
 * キャンバス確保が成功しているかを、背景の白塗り直後に右下 1 画素だけ読んで判定する。
 * 確保に失敗した環境（面積上限超過など）では白塗りが反映されず透明黒のままになるため、
 * アルファが 255（不透明）であることで成否を見分けられる。純白まで要求しないのは、
 * 色管理やテストスタブの差異で誤って失敗と判定しないため。
 */
function isBackgroundPainted(ctx: CanvasRenderingContext2D, x: number, y: number): boolean {
  // getImageData を持たない環境では判定不能とみなして続行する（確認できないことを理由に出力を止めない）。
  if (typeof ctx.getImageData !== "function") return true;
  try {
    return ctx.getImageData(x, y, 1, 1).data[3] === 255;
  } catch {
    // 画像を 1 枚も描いていない自前キャンバスの 1x1 読み出しで例外になるのは、汚染でも引数不正でもなく
    // 確保そのものが成立していない場合だけ（面積上限超過を readback 例外で示すブラウザがある）。
    return false;
  }
}

export async function renderCanvasExportImage(
  objects: readonly CanvasObject[],
  // 出力対象のキャンバス矩形（サイズプリセットに応じた原点＋寸法）。ラスタはこの矩形そのものを表すため、
  // 矩形の外側（キャンバス外）に置かれたオブジェクトはラスタ端でクリップされ、印刷/PDF はキャンバス範囲のみを含む。
  canvasRect: CanvasFrame,
  exportWidthPx: number = EXPORT_WIDTH_PX,
  // excludeUnpublishedSrc=true のとき、未確定のローカル専用画像（blob: URL）を全体像へ描かない。
  // Connect Note へ外部配信するスナップショットでは、サーバ未公開の本人ローカル画像を外部へ載せないために使う。
  // 利用者自身の手動 PDF エクスポート（既定 false）では、自分の blob プレビューもそのまま出力する。
  // format は出力ラスタの形式（既定 "png"）。等倍級の巨大ラスタを pdf-lib の PNG 埋め込みへ通すと
  // 全画素デコードと JS deflate でメインスレッドが停止するため、ダウンロード経路は "jpeg" を要求する。
  options?: {
    readonly excludeUnpublishedSrc?: boolean | undefined;
    readonly format?: ExportRasterFormat | undefined;
    /** リンクカードの imageUrl を読み込み可能な URL へ解決する（未指定なら読み込まない）。 */
    readonly resolveLinkCardImageUrl?: LinkCardImageResolver | undefined;
  }
): Promise<string> {
  // 大規模キャンバス向けの自動縮小で幅を可変にする。高さ・スケールは幅とキャンバス矩形から比例算出するため、
  // 幅を下げれば全オブジェクト・グリッド・フォントが一様に縮小し、生成 PNG/PDF のサイズも下がる。
  const width = Math.max(1, Math.round(exportWidthPx));
  const height = Math.round((canvasRect.h / canvasRect.w) * width);
  const format: ExportRasterFormat = options?.format ?? "png";
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";

  // ステージ座標 → ラスタ座標のスケール（現在のキャンバス矩形基準）。
  const scaleX = width / canvasRect.w;
  const scaleY = height / canvasRect.h;

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  // 確保確認は EXPORT_WIDTH_PX を超える幅の段だけに入れる。現行と同じ規模（自動保存スナップショットの
  // 2000px、ラダー最終段の 3508px）では確保失敗を想定しないため、読み出しによるフラッシュを持ち込まない。
  // 大きい段では、失敗が確定している全オブジェクト描画とエンコードを省ける利益が読み出しコストを上回る。
  if (width > EXPORT_WIDTH_PX && !isBackgroundPainted(ctx, width - 1, height - 1)) return "";
  drawGrid(ctx, width, height, canvasRect, scaleX, scaleY);

  // コメントスレッド（ピン）はビューポート固定の UI 装飾であり、エクスポートには描画しない。
  const drawable = objects.filter((object) => isDrawableObject(object.type));
  // 画像オブジェクト分のみ先に Image を読み込み、ペイント順序を維持するため描画順で整列する
  // （セクション帯 → 通常帯 → 各帯内は zOrder。Web 表示のサブレイヤ分離と同じ規則）。
  const ordered = drawable.slice().sort(compareCanvasObjectsForRender);
  const imageBySrc = await preloadImages(ordered, options?.excludeUnpublishedSrc ?? false, {
    resolveLinkCardImageUrl: options?.resolveLinkCardImageUrl,
  });
  // コネクタは端点オブジェクトの現在位置から都度ルーティングするため id 索引を渡す。
  const objectsById = new Map(drawable.map((object) => [object.id, object]));

  for (const object of ordered) {
    ctx.save();
    // キャンバス矩形の原点分だけオフセットしてラスタへ写す（原点が負値の大サイズでも正しく配置される）。
    ctx.translate(
      (object.x - canvasRect.x + object.w / 2) * scaleX,
      (object.y - canvasRect.y + object.h / 2) * scaleY
    );
    ctx.rotate(((object.rotation ?? 0) * Math.PI) / 180);
    ctx.translate((-object.w / 2) * scaleX, (-object.h / 2) * scaleY);
    drawObject(ctx, object, scaleX, scaleY, imageBySrc, objectsById);
    ctx.restore();
  }

  // toDataURL は確保失敗時に data:, を返したり、未対応 MIME を無視して別形式を返したりし得る。
  // 要求した形式と一致する非空の data URL 以外は、その幅の失敗として扱い呼び出し側へ降格させる
  // （要求と違う形式を受け入れると、避けたい「等倍 PNG を pdf-lib へ通す」経路へ戻ってしまう）。
  try {
    const dataUrl =
      format === "jpeg"
        ? canvas.toDataURL("image/jpeg", EXPORT_JPEG_QUALITY)
        : canvas.toDataURL("image/png");
    return isImageDataUrlOf(dataUrl, format) ? dataUrl : "";
  } catch {
    return "";
  }
}

/**
 * 既に開いている印刷用ウィンドウへ、生成中であることを示す最小限の HTML を書き込む。
 * 等倍化でラスタ生成に数秒かかるため、その間ウィンドウが白紙のままだと利用者が故障と区別できない。
 * 完成した画像は openPrintWindowWithImage が document.open() から書き直して上書きする。
 */
export function openPrintWindowPlaceholder(
  printWindow: Window,
  title: string,
  message: string = PRINT_WINDOW_GENERATING_MESSAGE
): void {
  const sanitizedTitle = escapeHtml(title);
  const sanitizedMessage = escapeHtml(message);
  // 補間値は直前で escapeHtml() 済みの sanitizedTitle / sanitizedMessage だけ。
  // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
  const html = `<!doctype html>
<html lang="ja">
<head>
<meta charset="utf-8" />
<title>${sanitizedTitle}</title>
<style>
  html, body { margin: 0; padding: 0; background: #ffffff; }
  body {
    display: flex; align-items: center; justify-content: center; min-height: 100vh;
    font-family: system-ui, sans-serif; color: #333333;
  }
</style>
</head>
<body>
<p>${sanitizedMessage}</p>
</body>
</html>`;
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
}

/**
 * 既に開いている印刷用ウィンドウへ、data URL 化した画像を含む HTML を書き込み印刷ダイアログを呼び出す。
 * 利用者は印刷先に「PDF として保存」を選び、PDF ファイルとして書き出す。
 *
 * ウィンドウはポップアップブロックを避けるため、呼び出し元のクリック同期処理で先に
 * `window.open()` して渡す（user activation が有効なうちに開く）。
 * 連打防止は呼び出し元（AppMenu の busy 状態）で行う。
 *
 * 戻り値: 書き込んで印刷を開始できたら true、許可形式以外の dataUrl でウィンドウを閉じたら false。
 * 呼び出し元は false を「静かにウィンドウが閉じただけ」で終わらせず、利用者へ失敗を通知する。
 */
export function openPrintWindowWithImage(
  printWindow: Window,
  dataUrl: string,
  title: string,
  options?: { readonly resultToken?: string | undefined }
): boolean {
  // 許可する画像 data URL（PNG / JPEG）以外は src 属性へ埋めずにウィンドウを閉じる。
  if (!isImageDataUrlOf(dataUrl, "png") && !isImageDataUrlOf(dataUrl, "jpeg")) {
    printWindow.close();
    return false;
  }
  const sanitizedTitle = escapeHtml(title);
  const resultToken = options?.resultToken;
  if (resultToken !== undefined && !/^[a-z0-9]+$/.test(resultToken)) {
    throw new Error("印刷用画像の試行トークンが不正です");
  }
  const notifyLoaded = resultToken
    ? `if(window.opener){window.opener.postMessage({type:'${PDF_EXPORT_IMAGE_RESULT_MESSAGE_TYPE}',token:'${resultToken}',result:'loaded'},'/');}`
    : "";
  const notifyDecodeFailed = resultToken
    ? `if(window.opener){window.opener.postMessage({type:'${PDF_EXPORT_IMAGE_RESULT_MESSAGE_TYPE}',token:'${resultToken}',result:'decode-failed'},'/');}`
    : "";
  // ページに対して画像を全幅で配置し、横向き印刷で 1 枚に収まりやすいよう @page を指定する。
  // 印刷後はウィンドウを自動で閉じる。
  // onerror は等倍ラスタのデコード失敗を受ける。これを扱わないと印刷ダイアログが永久に開かず、
  // 利用者は無言のまま待ち続けることになるため、失敗を明示して決定論的なダウンロード経路へ案内する。
  // 補間値は、標準 Base64 だけへ狭めた画像 data URL（上の isImageDataUrlOf。`"` や制御文字は
  // 通らないため属性境界を破れない）・escapeHtml() 済みの表題・/^[a-z0-9]+$/ を通した試行
  // トークンだけで、任意入力を素で埋めていない。
  // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
  const html = `<!doctype html>
<html lang="ja">
<head>
<meta charset="utf-8" />
<title>${sanitizedTitle}</title>
<style>
  @page { size: A4 landscape; margin: 8mm; }
  html, body { margin: 0; padding: 0; background: #ffffff; }
  body { display: flex; align-items: center; justify-content: center; min-height: 100vh; }
  img { width: 100%; height: auto; max-width: 100%; }
  @media print {
    body { min-height: 0; }
    img { width: 100%; height: auto; page-break-inside: avoid; }
  }
</style>
</head>
<body>
<img src="${dataUrl}" alt="${sanitizedTitle}" onload="${notifyLoaded}setTimeout(function(){window.focus();window.print();}, 50);" onerror="${notifyDecodeFailed}document.body.textContent='${PRINT_WINDOW_DECODE_ERROR_MESSAGE}';" />
<script>
window.addEventListener('afterprint', function(){ window.close(); });
</script>
</body>
</html>`;
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  return true;
}

/** 印刷ウィンドウから、現在の試行に対応する画像表示結果を待つ。 */
export function waitForPrintWindowImageResult(
  printWindow: Window,
  token: string,
  options?: { readonly timeoutMs?: number | undefined; readonly pollMs?: number | undefined }
): Promise<PrintWindowImageWait> {
  const timeoutMs = options?.timeoutMs ?? PRINT_WINDOW_IMAGE_RESULT_TIMEOUT_MS;
  const pollMs = options?.pollMs ?? PRINT_WINDOW_CLOSED_POLL_MS;
  return new Promise<PrintWindowImageWait>((resolve) => {
    let settled = false;
    let poll = 0;
    let timer = 0;
    const onMessage = (event: MessageEvent): void => {
      if (event.source !== printWindow || event.origin !== window.location.origin) return;
      const data: unknown = event.data;
      if (typeof data !== "object" || data === null) return;
      const record = data as Record<string, unknown>;
      if (record["type"] !== PDF_EXPORT_IMAGE_RESULT_MESSAGE_TYPE || record["token"] !== token) {
        return;
      }
      const result = record["result"];
      if (result === "loaded" || result === "decode-failed") finish(result);
    };
    const finish = (value: PrintWindowImageWait): void => {
      if (settled) return;
      settled = true;
      window.removeEventListener("message", onMessage);
      window.clearInterval(poll);
      window.clearTimeout(timer);
      resolve(value);
    };

    window.addEventListener("message", onMessage);
    poll = window.setInterval(() => {
      if (printWindow.closed) finish("closed");
    }, pollMs);
    timer = window.setTimeout(() => finish("timeout"), timeoutMs);
    if (printWindow.closed) finish("closed");
  });
}

/** 印刷経路が取り得る終端。 */
export type PrintExportOutcome =
  | { readonly status: "printing"; readonly width: number }
  | { readonly status: "render-failed" }
  | { readonly status: "decode-failed"; readonly lastWidth: number }
  | { readonly status: "window-closed" }
  | { readonly status: "timeout"; readonly width: number };

/**
 * 印刷画像を解像度ラダー順に生成し、別ウィンドウで表示できた段だけを印刷する。
 * デコード失敗時は同じウィンドウを案内表示へ戻してから次段を直列に試す。
 */
export async function exportCanvasToPrintWindow(
  printWindow: Window,
  objects: readonly CanvasObject[],
  canvasRect: CanvasFrame,
  title: string,
  options?: {
    readonly waitForImageResult?:
      | ((printWindow: Window, token: string) => Promise<PrintWindowImageWait>)
      | undefined;
    /** リンクカードの imageUrl を読み込み可能な URL へ解決する（未指定なら読み込まない）。 */
    readonly resolveLinkCardImageUrl?: LinkCardImageResolver | undefined;
  }
): Promise<PrintExportOutcome> {
  const waitForImageResult = options?.waitForImageResult ?? waitForPrintWindowImageResult;
  if (printWindow.closed) return { status: "window-closed" };
  openPrintWindowPlaceholder(printWindow, title);

  const widths = buildManualExportWidthLadder(canvasRect);
  let decodeFailures = 0;
  let lastWidth = widths[0] ?? EXPORT_WIDTH_PX;
  for (let index = 0; index < widths.length; index += 1) {
    const width = widths[index];
    if (width === undefined) continue;
    if (printWindow.closed) return { status: "window-closed" };

    const dataUrl = await renderCanvasExportImage(objects, canvasRect, width, {
      excludeUnpublishedSrc: false,
      format: "png",
      resolveLinkCardImageUrl: options?.resolveLinkCardImageUrl,
    });
    if (!dataUrl) {
      console.warn(`[pdf-export] 幅 ${width}px のラスタ生成に失敗したため降格します`);
      continue;
    }
    console.info(`[pdf-export] 印刷 / PDF 出力のラスタ生成に成功した解像度: ${width}px (png)`);
    if (printWindow.closed) return { status: "window-closed" };

    const token = createPrintImageResultToken();
    if (!openPrintWindowWithImage(printWindow, dataUrl, title, { resultToken: token })) {
      return { status: "render-failed" };
    }
    const result = await waitForImageResult(printWindow, token);
    lastWidth = width;
    switch (result) {
      case "loaded":
        console.info(`[pdf-export] 印刷ウィンドウで表示できた解像度: ${width}px`);
        return { status: "printing", width };
      case "decode-failed":
        decodeFailures += 1;
        console.warn(
          `[pdf-export] 幅 ${width}px の画像を印刷ウィンドウで表示できなかったため降格します`
        );
        if (index < widths.length - 1 && !printWindow.closed) {
          openPrintWindowPlaceholder(printWindow, title, PRINT_WINDOW_RETRY_MESSAGE);
        }
        break;
      case "closed":
        return { status: "window-closed" };
      case "timeout":
        return { status: "timeout", width };
    }
  }

  if (printWindow.closed) return { status: "window-closed" };
  if (decodeFailures > 0) {
    openPrintWindowPlaceholder(printWindow, title, PRINT_WINDOW_DECODE_ERROR_MESSAGE);
    return { status: "decode-failed", lastWidth };
  }
  return { status: "render-failed" };
}

// object-fit: cover 相当で画像を矩形 (dx,dy,dw,dh) に描画する（短辺基準で中央クロップ）。
// アバタースタンプを丸クリップ内へ歪みなく描くために使う。
function drawImageCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  dx: number,
  dy: number,
  dw: number,
  dh: number
): void {
  const iw = img.naturalWidth || dw;
  const ih = img.naturalHeight || dh;
  if (iw <= 0 || ih <= 0) {
    ctx.drawImage(img, dx, dy, dw, dh);
    return;
  }
  const scale = Math.max(dw / iw, dh / ih);
  const sw = dw / scale;
  const sh = dh / scale;
  const sx = (iw - sw) / 2;
  const sy = (ih - sh) / 2;
  ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
}

function drawGrid(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  canvasRect: CanvasFrame,
  scaleX: number,
  scaleY: number
): void {
  // グリッド間隔・ドットサイズも描画幅に比例して縮小し、低解像度化してもオブジェクトと同じ縮率で
  // 縮む（全体像の見た目＝グリッド密度を保つ）。既定幅（EXPORT_WIDTH_PX）では従来どおり 80px / 2px。
  const factor = width / EXPORT_WIDTH_PX;
  const step = Math.max(1, 80 * factor);
  const dot = Math.max(1, 2 * factor);
  // 編集画面のグリッド（canvas-grid）と同様に、キャンバス矩形原点の位相ぶんずらして出力物のグリッド
  // 基準位置を編集画面と揃える（大サイズで原点が負値でも一致させる）。
  const phaseX = (((canvasRect.x % CANVAS_GRID_PX) + CANVAS_GRID_PX) % CANVAS_GRID_PX) * scaleX;
  const phaseY = (((canvasRect.y % CANVAS_GRID_PX) + CANVAS_GRID_PX) % CANVAS_GRID_PX) * scaleY;
  ctx.fillStyle = "rgba(26, 26, 46, 0.08)";
  for (let x = -phaseX; x < width; x += step) {
    for (let y = -phaseY; y < height; y += step) {
      ctx.fillRect(x, y, dot, dot);
    }
  }
}

function drawObject(
  ctx: CanvasRenderingContext2D,
  object: CanvasObject,
  scaleX: number,
  scaleY: number,
  imageBySrc: Map<string, HTMLImageElement>,
  objectsById: Map<string, CanvasObject>
): void {
  const w = Math.max(1, object.w * scaleX);
  const h = Math.max(1, object.h * scaleY);
  switch (object.type) {
    case "section": {
      // セクションは薄い塗り + 枠線 + 左上タイトルで領域を示す（Web 表示・サムネイルと同じ見た目方針）。
      const fill = object.fill ?? SECTION_DEFAULT_FILL;
      ctx.save();
      ctx.globalAlpha = 0.16;
      ctx.fillStyle = fill;
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
      ctx.strokeStyle = fill;
      ctx.lineWidth = Math.max(1, 2 * Math.min(scaleX, scaleY));
      ctx.strokeRect(0, 0, w, h);
      const displayText = displayTextOfLinkifiedText(object.text);
      if (displayText.length > 0) {
        // タイトルは枠内左上へ 1 行のみ描画する（タイトルは単一行運用のため折り返し不要）。
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, 0, w, h);
        ctx.clip();
        const fontPx = Math.max(10, 20 * scaleY);
        ctx.fillStyle = "#1a1a2e";
        ctx.font = `600 ${fontPx}px sans-serif`;
        ctx.fillText(displayText, 8 * scaleX, 8 * scaleY + fontPx);
        ctx.restore();
      }
      return;
    }
    case "sticky":
      ctx.fillStyle = object.color ?? "#fff5b3";
      ctx.strokeStyle = "rgba(30, 41, 59, 0.4)";
      ctx.lineWidth = 1;
      ctx.fillRect(0, 0, w, h);
      ctx.strokeRect(0, 0, w, h);
      // 本文・署名は付箋の枠でクリップしてから描画する（画面側 sticky-object.tsx の overflow:hidden と同等）。
      // 極小サイズ（padding と署名領域を引くと描画可能領域が 0 以下になる）付箋でも、PDF が枠外へ
      // はみ出して描画されないようにする。
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, w, h);
      ctx.clip();
      // 署名の文字サイズは画面表示（sticky-object.tsx）と同じく付箋サイズに連動させる（スケール前 px）。
      const stickySigFontPx = object.updatedByName
        ? computeStickySignatureFontPx({ boxWidth: object.w, boxHeight: object.h })
        : 0;
      // 背景色に応じた本文・署名の文字色を画面表示（sticky-object.tsx）と同じ判定で解決する
      // （濃い背景では白へ反転）。これにより PDF 変換でも見た目どおりの文字色になる。
      const stickyTextPalette = resolveStickyTextPalette(object.color);
      const tableBodyPalette = resolveTableBodyPalette({
        bodyColor: stickyTextPalette.body,
        fill: markdownTableBackgroundOf(object),
      });
      if (object.text) {
        // 画面表示（sticky-object.tsx）と同様に、付箋サイズへ収まる文字サイズを自動算出する。
        const padX = STICKY_PADDING_PX * scaleX;
        const padY = STICKY_PADDING_PX * scaleY;
        // updatedByName がある付箋は、画面と同じく下部に署名領域（署名占有分＋安全余白）を確保する。
        // 画面表示（sticky-object.tsx）と同じ computeStickyBodyReservePx を使い、本文が欠けず署名とも
        // 重ならない見た目を PDF でも一致させる。
        const reserveY = object.updatedByName
          ? computeStickyBodyReservePx({ boxWidth: object.w, boxHeight: object.h }) * scaleY
          : 0;
        const availWidth = w - padX * 2;
        const availHeight = h - padY * 2 - reserveY;
        const align = object.textAlign ?? "left";
        const fontPx = computeStickyExportFontPx(
          ctx,
          object.text,
          availWidth,
          availHeight,
          scaleY,
          align,
          object.tableColumnWidths
        );
        drawMarkdownText(ctx, {
          align,
          baseFontPx: fontPx,
          color: stickyTextPalette.body,
          maxWidth: availWidth,
          // 表の外枠線（画面 1px）は文字と同じ倍率で換算する（フィット計算 computeStickyExportFontPx と同じ）。
          pxScale: scaleY,
          calloutOnDark: prefersLinkColorOnDark(markdownTableBackgroundOf(object)),
          tableBodyPalette,
          text: object.text,
          x: padX,
          y: padY,
          ...(object.tableColumnWidths === undefined
            ? {}
            : { tableColumnWidths: object.tableColumnWidths }),
        });
      }
      // 画面表示（sticky-object.tsx の左下署名）と見た目を揃えるため、最終更新者名を PDF にも描画する。
      // これにより、上で確保した下部の署名領域（reserveY）が無駄な空白にならず、出力が画面と一致する。
      if (object.updatedByName) {
        drawStickySignature(
          ctx,
          object.updatedByName,
          w,
          h,
          scaleX,
          scaleY,
          stickySigFontPx,
          stickyTextPalette.signature
        );
      }
      ctx.restore();
      return;
    case "shape":
      ctx.strokeStyle = object.stroke ?? SHAPE_DEFAULT_STROKE;
      ctx.fillStyle = object.fill ?? "rgba(255,255,255,0)";
      ctx.lineWidth = Math.max(
        1,
        (object.strokeWidth ?? SHAPE_DEFAULT_STROKE_WIDTH) * Math.min(scaleX, scaleY)
      );
      applyCanvasStrokePattern(ctx, ctx.lineWidth, object.strokePattern);
      ctx.lineCap = resolveStrokePattern(object.strokePattern) === "dotted" ? "round" : "butt";
      {
        const inset = 2;
        const rectW = w - inset * 2;
        const rectH = h - inset * 2;
        drawShape(ctx, object.shapeKind, w, h, object.arrowFromEdge, object.arrowFromCorner, {
          x: inset,
          y: inset,
          w: rectW,
          h: rectH,
          radius: resolveRectCornerRadius(rectW, rectH, Math.min(scaleX, scaleY)),
        });
      }
      return;
    case "text":
      // 行送りは画面の line-height と同じ 1.5（STICKY_LINE_HEIGHT_RATIO）で描画する
      // （従来の 1.4 から意図的に変更し、画面と PDF の見た目を一致させる）。
      {
        const background = resolveTextBackground(object.fill);
        const bodyColor = object.color ?? TEXT_DEFAULT_COLOR;
        const tableBodyPalette = resolveTableBodyPalette({
          bodyColor,
          fill: markdownTableBackgroundOf(object),
        });
        // 基準フォントには可読性の下限（10px）を掛ける。部分書式の fontSize も同じ倍率で
        // そろえないと、下限が効いた小さな出力で「基底 16px と部分 32px がほぼ同じ大きさ」に
        // なってしまう。基底に実際に適用された倍率を求めて部分書式へ渡し、画面上の
        // 大小関係（部分 / 基底の比）を出力でも保つ。
        const baseObjectFontPx = Math.max(1, object.fontSize ?? 16);
        const baseFontPx = Math.max(10, baseObjectFontPx * scaleY);
        drawMarkdownText(ctx, {
          align: object.textAlign ?? "left",
          baseFontPx,
          forceBold: object.bold === true,
          color: bodyColor,
          maxWidth: w,
          // 表の外枠線（画面 1px）は出力スケールで換算する。可読性の下限（10px）が効く小さな出力では
          // 文字の倍率と一致しないが、その場合は本文全体が比例より大きく描かれるため列幅の一致は求めない。
          pxScale: scaleY,
          calloutOnDark: prefersLinkColorOnDark(markdownTableBackgroundOf(object)),
          tableBodyPalette,
          text: object.text,
          ...(object.inlineStyles !== undefined ? { inlineStyles: object.inlineStyles } : {}),
          ...(object.tableColumnWidths === undefined
            ? {}
            : { tableColumnWidths: object.tableColumnWidths }),
          inlineFontScale: baseFontPx / baseObjectFontPx,
          x: 0,
          y: 0,
          ...(background !== undefined
            ? { background: { color: background, width: w, minHeight: h } }
            : {}),
        });
      }
      return;
    case "table":
      drawTableObject(ctx, object, w, h, scaleX, scaleY);
      return;
    case "image": {
      const img = object.src ? imageBySrc.get(object.src) : undefined;
      if (img) {
        ctx.drawImage(img, 0, 0, w, h);
      } else {
        drawMediaPlaceholder(ctx, w, h, "#dbeafe");
      }
      return;
    }
    case "video":
      drawMediaPlaceholder(ctx, w, h, "#111827");
      return;
    case "link-card": {
      drawMediaPlaceholder(ctx, w, h, "#eef2ff");
      const cardImg = object.imageUrl ? imageBySrc.get(object.imageUrl) : undefined;
      if (cardImg) {
        const cardW = w;
        const cardH = h * 0.55;
        ctx.drawImage(cardImg, 0, 0, cardW, cardH);
      }
      ctx.fillStyle = "#1e3a8a";
      ctx.font = `${Math.max(10, 14 * scaleY)}px sans-serif`;
      ctx.fillText(object.title.slice(0, 64), 8 * scaleX, Math.min(h - 8, h * 0.65));
      return;
    }
    case "pen-stroke": {
      ctx.strokeStyle = object.color;
      ctx.lineWidth = Math.max(1, object.thickness * scaleX);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      applyCanvasStrokePattern(ctx, ctx.lineWidth, object.strokePattern);
      ctx.beginPath();
      const outputPoints = object.points.map((point) => ({
        x: (point.x - object.x) * scaleX,
        y: (point.y - object.y) * scaleY,
      }));
      outputPoints.forEach((point, index) => {
        if (index === 0) ctx.moveTo(point.x, point.y);
        else ctx.lineTo(point.x, point.y);
      });
      ctx.stroke();
      fillDottedStrokeStartFallback(
        ctx,
        outputPoints,
        ctx.lineWidth,
        object.strokePattern,
        object.color
      );
      return;
    }
    case "stamp": {
      // アバタースタンプは、プリロード済みの実アバター画像を丸く描画してキャンバス本体と見た目を
      // 一致させる（複数人のアバターを出力物上でも区別できるようにする）。画像が取得できなかった
      // 場合のみ人物アイコン絵文字へフォールバックする。
      if (object.stampKind === "avatar") {
        // 同一 URL が image / link-card 経由でプリロードされていると、非許可 avatarSrc でも
        // imageBySrc に載って描画されうる。描画直前にも判定し、stamp-object（DOM 描画）と
        // 完全一致させる（安全なラスタ data:image/ のみ描画、それ以外は 👤 へフォールバック）。
        const avatarImg =
          isSnapshotAvatarImageUrl(object.avatarSrc) && object.avatarSrc
            ? imageBySrc.get(object.avatarSrc)
            : undefined;
        if (avatarImg) {
          ctx.save();
          ctx.beginPath();
          // 画面側（stamp-object）は border-radius:50% で、非正方形（w!==h）のとき楕円クリップになる。
          // PDF も同じ楕円クリップへ揃える（正円固定だと横長/縦長スタンプだけ見た目がずれる）。
          ctx.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
          ctx.closePath();
          ctx.clip();
          // object-fit: cover 相当（短辺基準で中央クロップ）。
          drawImageCover(ctx, avatarImg, 0, 0, w, h);
          ctx.restore();
          return;
        }
      }
      // 絵文字スタンプ（およびアバター画像が取得できなかった場合）はグリフを中央に描画する。
      // フォントは OS のカラー絵文字に委ねる（Canvas2D の描画結果はモノクロになる環境もあるが、
      // PNG 化前提のため最低限読める形にする）。
      const glyph =
        object.stampKind === "avatar" ? "👤" : (STAMP_PDF_GLYPH[object.stampKind] ?? "❔");
      const sizePx = Math.max(12, Math.min(w, h) * 0.85);
      ctx.fillStyle = "#1a1a2e";
      ctx.font = `${sizePx}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(glyph, w / 2, h / 2);
      // テキスト系設定をリセットして以降の描画へ影響しないようにする。
      ctx.textAlign = "start";
      ctx.textBaseline = "alphabetic";
      return;
    }
    case "connector": {
      const fromObj = objectsById.get(object.from.objectId);
      const toObj = objectsById.get(object.to.objectId);
      if (!fromObj || !toObj) return;
      const route = computeConnectorRoute(
        { x: fromObj.x, y: fromObj.y, w: fromObj.w, h: fromObj.h, rotation: fromObj.rotation ?? 0 },
        object.from.side,
        { x: toObj.x, y: toObj.y, w: toObj.w, h: toObj.h, rotation: toObj.rotation ?? 0 },
        object.to.side
      );
      if (route.length < 2) return;
      // ルートはステージ座標。ctx は object.x/object.y に平行移動済みのため、相対座標へ変換する。
      const toLocal = (p: { x: number; y: number }): { x: number; y: number } => ({
        x: (p.x - object.x) * scaleX,
        y: (p.y - object.y) * scaleY,
      });
      ctx.strokeStyle = object.stroke ?? CONNECTOR_DEFAULT_STROKE;
      ctx.lineWidth = Math.max(
        1,
        (object.strokeWidth ?? CONNECTOR_DEFAULT_STROKE_WIDTH) * Math.min(scaleX, scaleY)
      );
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      route.forEach((point, index) => {
        const l = toLocal(point);
        if (index === 0) ctx.moveTo(l.x, l.y);
        else ctx.lineTo(l.x, l.y);
      });
      ctx.stroke();
      const tip = toLocal(route[route.length - 1]!);
      const before = toLocal(route[route.length - 2]!);
      const head = arrowHeadPoints(tip, before, 12 * Math.min(scaleX, scaleY));
      if (head) {
        ctx.beginPath();
        ctx.moveTo(head.left.x, head.left.y);
        ctx.lineTo(tip.x, tip.y);
        ctx.lineTo(head.right.x, head.right.y);
        ctx.stroke();
      }
      return;
    }
  }
}

/** 表図形を画面と同じ列幅・行高・配色で描き、外枠からはみ出す内容を切り取る。 */
function drawTableObject(
  ctx: CanvasRenderingContext2D,
  object: Extract<CanvasObject, { readonly type: "table" }>,
  w: number,
  h: number,
  scaleX: number,
  scaleY: number
): void {
  const fontPx = Math.max(1, object.fontSize ?? 16);
  // 行高の見積もりには倍率をかける前の列幅（画面と同じ px）を渡す。
  const baseColumnWidths = resolveTableObjectColumnWidths({
    cells: object.cells,
    columnFractions: object.columnFractions,
    availablePx: Math.max(0, object.w - TABLE_BORDER_PX * 2),
    fontPx,
  });
  const columnWidths = baseColumnWidths.map((value) => value * scaleX);
  const rowHeights = resolveTableObjectRowHeights({
    // 折り返し行数は、実際に描く表示テキスト（記法を解釈した後）で測る。
    cellTexts: object.cells.map((row) => row.map(tableObjectCellDisplayText)),
    columnWidthsPx: baseColumnWidths,
    // 部分書式（run）の文字サイズも行高へ反映する。渡さないと、大きい文字の行だけが
    // 画面より低く見積もられ、下の行が行高で打ち切られて欠ける。
    cellInlineStyles: object.cellInlineStyles,
    availablePx: Math.max(0, object.h - TABLE_BORDER_PX * 2),
    fontPx,
  }).map((value) => value * scaleY);
  const borderX = Math.max(1, TABLE_BORDER_PX * scaleX);
  const borderY = Math.max(1, TABLE_BORDER_PX * scaleY);
  // 表全体の実寸は、共有関数で解決した各列幅の合計から求める。
  const tableWidth = columnWidths.reduce((sum, value) => sum + value, 0);
  // 外枠の高さは、共有関数で解決した各行高の合計に上下の罫線を加える。
  const tableHeight = rowHeights.reduce((sum, value) => sum + value, 0) + borderY * 2;

  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, w, h);
  ctx.clip();
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, w, h);

  let rowTop = borderY;
  for (const [rowIndex, rowHeight] of rowHeights.entries()) {
    if (rowTop >= h) break;
    if (rowIndex === 0) {
      ctx.fillStyle = MARKDOWN_TABLE_HEADER_BACKGROUND_COLOR;
      ctx.fillRect(borderX, rowTop, tableWidth, rowHeight);
    } else if (rowIndex % 2 === 0) {
      // tbody の偶数行（表全体では 3 行目、5 行目…）だけを薄く塗る。
      ctx.fillStyle = MARKDOWN_TABLE_ROW_STRIPE_COLOR;
      ctx.fillRect(borderX, rowTop, tableWidth, rowHeight);
    }
    rowTop += rowHeight;
  }

  // 外枠と列境界。ヘッダ内の列境界だけは画面と同じ白色へ切り替える。
  ctx.fillStyle = MARKDOWN_TABLE_BORDER_COLOR;
  strokeRectBorder(ctx, 0, 0, tableWidth + borderX * 2, tableHeight, Math.min(borderX, borderY));
  let columnLeft = borderX;
  for (let column = 1; column < columnWidths.length; column += 1) {
    columnLeft += columnWidths[column - 1]!;
    ctx.fillStyle = MARKDOWN_TABLE_BORDER_COLOR;
    ctx.fillRect(
      columnLeft,
      borderY + (rowHeights[0] ?? 0),
      borderX,
      Math.max(0, h - (rowHeights[0] ?? 0) - borderY)
    );
    ctx.fillStyle = MARKDOWN_TABLE_HEADER_TEXT_COLOR;
    ctx.fillRect(columnLeft, borderY, borderX, rowHeights[0] ?? 0);
  }

  const outputFontPx = fontPx * scaleY;
  const padX = (TABLE_CELL_PADDING_EM / 2) * fontPx * scaleX;
  // 計測は ctx.font を切り替えるだけで状態を持たないため、セルごとに作り直さない。
  const measure = createCanvasMeasure(ctx);
  rowTop = borderY;
  for (const [rowIndex, row] of object.cells.entries()) {
    const rowHeight = rowHeights[rowIndex] ?? 0;
    if (rowTop >= h) break;
    columnLeft = borderX;
    for (const [columnIndex, cell] of row.entries()) {
      const columnWidth = columnWidths[columnIndex] ?? 0;
      if (columnLeft >= w) break;
      if (cell.length > 0 && columnWidth > padX * 2) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(columnLeft, rowTop, columnWidth, rowHeight);
        ctx.clip();
        ctx.textBaseline = "alphabetic";
        // 画面と同じ表示 span 列（記法のインライン装飾を解釈済み）を、本文中の Markdown 表と
        // 同じ折り返し・グルーピングでレイアウトしてから描く。
        const segments = layoutInlineSpansForCanvas({
          baseFontPx: outputFontPx,
          bold: rowIndex === 0,
          // run の fontSize は保存 px なので、出力倍率で換算する（換算しないと拡大・縮小
          // 出力で部分書式の文字だけが画面と違う大きさになる）。
          inlineFontScale: outputFontPx / fontPx,
          maxWidth: Math.max(0, columnWidth - padX * 2),
          measure,
          // 画面と同じ部分書式（run）を渡し、色・サイズ・太字を画面と PDF でそろえる
          // （サムネイルは文字ではなく帯で描くため、部分書式を渡す先が無い）。
          spans: tableObjectCellSpans(cell, cellInlineStylesAt(object, rowIndex, columnIndex)),
        });
        drawLayoutSegments(
          ctx,
          // ベースラインが行の下端を越えた行は描かない（従来どおり行の高さで打ち切る）。
          segments.filter((segment) => segment.baselineY <= rowHeight),
          columnLeft + padX,
          rowTop,
          rowIndex === 0 ? MARKDOWN_TABLE_HEADER_TEXT_COLOR : (object.color ?? TEXT_DEFAULT_COLOR)
        );
        ctx.restore();
      }
      columnLeft += columnWidth;
    }
    rowTop += rowHeight;
  }
  ctx.restore();
}

// PDF / 印刷出力で使うスタンプ → 絵文字グリフのマッピング。
// 画面表示用（stamp-object.tsx の STAMP_KIND_GLYPH）と同じ値を持つが、
// 循環参照を避けるためにこのモジュール内で複製している。型は EmojiStampKind 基準にして、
// StampKind に絵文字種別が増減したらこのマップもキー追従が必要になるようにする（avatar は対象外）。
const STAMP_PDF_GLYPH: Record<EmojiStampKind, string> = {
  "thumbs-up": "👍",
  heart: "❤️",
  party: "🎉",
  grin: "😄",
  surprised: "😮",
  pray: "🙏",
  eyes: "👀",
  bulb: "💡",
  check: "✅",
  rocket: "🚀",
  fire: "🔥",
  smile: "😊",
  hundred: "💯",
  plus: "➕",
  cry: "😢",
  sob: "😭",
  disappointed: "😞",
  pensive: "😔",
  "broken-heart": "💔",
};

function drawShape(
  ctx: CanvasRenderingContext2D,
  shapeKind: "arrow" | "circle" | "rectangle",
  w: number,
  h: number,
  arrowFromEdge: ArrowEdge | undefined,
  arrowFromCorner: ArrowCorner | undefined,
  rect: {
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
    readonly radius: number;
  }
): void {
  if (shapeKind === "circle") {
    ctx.beginPath();
    ctx.ellipse(w / 2, h / 2, Math.max(1, w / 2 - 2), Math.max(1, h / 2 - 2), 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    return;
  }
  if (shapeKind === "arrow") {
    // 軸そろえ矢印は起点辺→対辺、対角矢印は起点角→対角へ描く。
    if (arrowFromEdge || arrowFromCorner) {
      const path = arrowFromEdge
        ? resolveArrowEdgePath(w, h, arrowFromEdge, 12, 6)
        : resolveArrowPath(w, h, arrowFromCorner!, 12, 6);
      ctx.beginPath();
      ctx.moveTo(path.tail.x, path.tail.y);
      ctx.lineTo(path.head.x, path.head.y);
      ctx.moveTo(path.wingA.x, path.wingA.y);
      ctx.lineTo(path.head.x, path.head.y);
      ctx.lineTo(path.wingB.x, path.wingB.y);
      ctx.stroke();
      return;
    }
    ctx.beginPath();
    ctx.moveTo(4, h / 2);
    ctx.lineTo(w - 12, h / 2);
    ctx.moveTo(w - 12, h / 2 - 6);
    ctx.lineTo(w - 4, h / 2);
    ctx.lineTo(w - 12, h / 2 + 6);
    ctx.stroke();
    return;
  }
  if (rect.w <= 0 || rect.h <= 0 || rect.radius < 0.5) {
    ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
    ctx.strokeRect(rect.x, rect.y, rect.w, rect.h);
    return;
  }
  ctx.beginPath();
  traceRoundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, rect.radius);
  ctx.fill();
  ctx.stroke();
}

function drawMediaPlaceholder(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  color: string
): void {
  ctx.fillStyle = color;
  ctx.strokeStyle = "rgba(30, 41, 59, 0.35)";
  ctx.lineWidth = 1;
  ctx.fillRect(0, 0, w, h);
  ctx.strokeRect(0, 0, w, h);
}

/** Markdown 表を含められる図形から、明暗判定に使う実際の背景色を返す。 */
function markdownTableBackgroundOf(
  object: Extract<CanvasObject, { readonly type: "sticky" | "text" }>
): string | undefined {
  // 付箋は color、テキスト図形は fill が背景色であるため、同名として一律に読まない。
  return object.type === "sticky" ? object.color : object.fill;
}

/**
 * 本文（生の Markdown テキスト）を、画面表示（MarkdownTextView）と同じ整形
 * （見出しの倍率 + 太字 / 箇条書き・番号付きリストのマーカー + ぶら下げインデント /
 * 本文色の引用の左罫 / 水平線の横罫 / 行単位の折り返し）で Canvas 2D へ描画する。
 * レイアウト計算は layoutMarkdownTextForCanvas に集約している。
 */
function drawMarkdownText(
  ctx: CanvasRenderingContext2D,
  options: {
    readonly align: CanvasTextAlign;
    readonly forceBold?: boolean | undefined;
    readonly baseFontPx: number;
    readonly color: string;
    readonly maxWidth: number;
    readonly text: string;
    /** 文字範囲だけへ適用する部分書式（正規形の run 配列）。 */
    readonly inlineStyles?: readonly TextInlineStyleRange[] | undefined;
    /** 部分書式の fontSize（保存値は px）へ掛ける出力スケール。 */
    readonly inlineFontScale?: number | undefined;
    /** 画面の CSS px を出力 px へ換算する倍率（表の外枠線・区切り線の太さと使用可能幅の換算）。 */
    readonly pxScale: number;
    /** callout の背景・左罫へ暗背景用の値を使うか。 */
    readonly calloutOnDark: boolean;
    /** 図形の塗りと実際の本文色から解決した表本文の不透明な配色。 */
    readonly tableBodyPalette: MarkdownTableBodyPalette;
    /** 本文中の表ごとの保存済み列幅（ブロック順）。未指定は自動列幅。 */
    readonly tableColumnWidths?: TableColumnWidthsList | undefined;
    /** 描画領域左上の X（px）。 */
    readonly x: number;
    /** 描画領域左上の Y（px）。 */
    readonly y: number;
    /** 本文の背後へ敷く背景。省略時は塗らない。 */
    readonly background?:
      | { readonly color: string; readonly width: number; readonly minHeight: number }
      | undefined;
  }
): void {
  const layout = layoutMarkdownTextForCanvas({
    align: resolveLayoutAlign(options.align),
    forceBold: options.forceBold,
    baseFontPx: options.baseFontPx,
    calloutOnDark: options.calloutOnDark,
    maxWidth: options.maxWidth,
    measure: createCanvasMeasure(ctx),
    pxScale: options.pxScale,
    bodyColor: options.color,
    tableBodyPalette: options.tableBodyPalette,
    text: options.text,
    ...(options.inlineStyles !== undefined ? { inlineStyles: options.inlineStyles } : {}),
    ...(options.inlineFontScale !== undefined ? { inlineFontScale: options.inlineFontScale } : {}),
    ...(options.tableColumnWidths === undefined
      ? {}
      : { tableColumnWidths: options.tableColumnWidths }),
  });
  const background = options.background;
  if (background) {
    ctx.fillStyle = background.color;
    ctx.fillRect(
      options.x,
      options.y,
      background.width,
      Math.max(background.minHeight, layout.totalHeight)
    );
  }
  // 水平線の横罫・引用の左罫・表の外枠 / 列ヘッダーは、テキストより先（背面）に描く。横罫の色は画面の
  // CSS 変数（--color-border）の明テーマ解決値と同じ共有定数、引用の左罫は画面の .canvas-md-quote
  // （currentColor）と同じく解決済みの本文色（濃い付箋では白、テキスト図形では文字色）を使う。
  // 表の外枠・列ヘッダー背景・区切り線は画面の `--ds-table-*` と同じ共有定数の色を使う。
  // コードブロックは面（塗り）と枠線（矩形の外周 1px）で、画面の `.canvas-md-code` と同じ色にする。
  // 本設計の記法を含まない本文では rects が空で、従来の出力は 1px も変わらない。
  for (const rect of layout.rects) {
    // callout のように type ごとに色が変わる矩形は、レイアウトが解決済みの色を持つ。
    ctx.fillStyle =
      rect.fill ?? resolveLayoutRectColor(rect.role, options.color, options.tableBodyPalette);
    if (rect.role === "code-border") {
      // 枠線の太さは画面の 1px を出力スケールで換算する（レイアウトの内側余白と同じ値）。
      strokeRectBorder(
        ctx,
        options.x + rect.x,
        options.y + rect.y,
        rect.w,
        rect.h,
        MARKDOWN_CODE_BORDER_PX * options.pxScale
      );
      continue;
    }
    if (rect.role === "task-box") {
      strokeRectBorder(
        ctx,
        options.x + rect.x,
        options.y + rect.y,
        rect.w,
        rect.h,
        2 * options.pxScale
      );
      continue;
    }
    if (rect.role === "task-box-checked") {
      const x = options.x + rect.x;
      const y = options.y + rect.y;
      ctx.fillRect(x, y, rect.w, rect.h);
      drawTaskCheckMark(ctx, x, y, rect.w, rect.h);
      continue;
    }
    if (
      rect.role === "table-body-bg" ||
      rect.role === "table-row-stripe" ||
      rect.role === "table-row-stripe-on-dark"
    ) {
      // 表本文の面は常に不透明な実色で描き、直前の描画処理の alpha を引き継がない。
      ctx.save();
      ctx.globalAlpha = 1;
      ctx.fillStyle = resolveLayoutRectColor(rect.role, options.color, options.tableBodyPalette);
      ctx.fillRect(options.x + rect.x, options.y + rect.y, rect.w, rect.h);
      ctx.restore();
      continue;
    }
    ctx.fillRect(options.x + rect.x, options.y + rect.y, rect.w, rect.h);
  }
  drawLayoutSegments(ctx, layout.segments, options.x, options.y, options.color);
  // 後続オブジェクトの描画へ寄せ設定を漏らさないよう既定へ戻す。
  ctx.textAlign = "left";
}

/**
 * レイアウト済みのセグメント列を描く（本文の Markdown と表図形のセルが共有する）。
 * 座標は `originX` / `originY` を原点とする相対値で、`fallbackColor` は部分書式の文字色を
 * 持たない区間の色。フォント・文字背景・取消線の扱いを 1 か所へ集約し、本文中の表と
 * 表図形とで装飾の見た目がずれないようにする。
 */
function drawLayoutSegments(
  ctx: CanvasRenderingContext2D,
  segments: readonly layoutMarkdownTextForCanvas.Segment[],
  originX: number,
  originY: number,
  fallbackColor: string
): void {
  for (const segment of segments) {
    ctx.font = canvasFontFor(segment.fontPx, {
      bold: segment.bold,
      italic: segment.italic === true,
      monospace: segment.monospace === true,
    });
    ctx.textAlign = segment.anchor;
    // 部分書式の文字背景とインラインコードの背景は、画面のインラインハイライトと同じく
    // glyph の背後だけを塗る。ベースラインから上へ約 0.8em、下へ約 0.2em の em ボックス相当を
    // 対象にし、インラインコードでは左右 padding のぶんだけ外側へ広げる。
    const padLeft = segment.padLeftPx ?? 0;
    const padRight = segment.padRightPx ?? 0;
    const glyphWidth = segment.width ?? 0;
    if (segment.background !== undefined && glyphWidth + padLeft + padRight > 0) {
      ctx.fillStyle = segment.background;
      ctx.fillRect(
        originX + segment.x - padLeft,
        originY + segment.baselineY - segment.fontPx * 0.8,
        glyphWidth + padLeft + padRight,
        segment.fontPx
      );
    }
    ctx.fillStyle = segment.color ?? fallbackColor;
    ctx.fillText(segment.text, originX + segment.x, originY + segment.baselineY);
    // 取消線は、ベースラインより上 0.3em の位置へ文字の実測幅ぶんの矩形で描く
    // （Canvas 2D に text-decoration が無いため）。
    if (segment.strikethrough === true && glyphWidth > 0) {
      ctx.fillRect(
        originX + segment.x,
        originY + segment.baselineY - segment.fontPx * 0.3,
        glyphWidth,
        Math.max(1, segment.fontPx * 0.06)
      );
    }
  }
}

/**
 * レイアウトの塗り矩形の役割を実際の色へ解決する。
 * 引用の左罫だけは本文色（画面の currentColor と同じ）で、他は画面の CSS 変数と同じ共有定数。
 */
function resolveLayoutRectColor(
  role: layoutMarkdownTextForCanvas.LayoutRect["role"],
  bodyColor: string,
  tableBodyPalette: MarkdownTableBodyPalette = {
    bodyBackground: MARKDOWN_TABLE_BODY_BACKGROUND_COLOR,
    stripeBackground: MARKDOWN_TABLE_ROW_STRIPE_COLOR,
    onDark: false,
  }
): string {
  switch (role) {
    case "rule":
      return MARKDOWN_RULE_COLOR;
    case "table-border":
      return MARKDOWN_TABLE_BORDER_COLOR;
    case "table-header-bg":
      return MARKDOWN_TABLE_HEADER_BACKGROUND_COLOR;
    case "table-header-sep":
      return MARKDOWN_TABLE_HEADER_TEXT_COLOR;
    case "table-body-bg":
      return tableBodyPalette.bodyBackground;
    case "table-row-stripe":
      return tableBodyPalette.stripeBackground;
    case "table-row-stripe-on-dark":
      return tableBodyPalette.stripeBackground;
    case "code-bg":
      return MARKDOWN_CODE_BACKGROUND_COLOR;
    case "code-border":
      return MARKDOWN_CODE_BORDER_COLOR;
    // callout の 2 役割はレイアウトが type ごとの色を rect.fill に載せるため、通常はここへ来ない。
    // `fill` が欠けた場合の保険として、既定 type（note）の明背景用の色を使う。
    case "callout-bg":
      return CALLOUT_COLORS.light.note.bg;
    case "callout-accent":
      return CALLOUT_COLORS.light.note.accent;
    case "task-box":
      return bodyColor;
    case "task-box-checked":
      return TASK_BOX_CHECKED_COLOR;
    case "quote-bar":
      return bodyColor;
    default:
      return assertNeverRole(role);
  }
}

/** チェック済み task のレ点を 2 本の線で描く。 */
function drawTaskCheckMark(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number
): void {
  ctx.save();
  ctx.strokeStyle = TASK_BOX_CHECK_COLOR;
  ctx.lineWidth = Math.max(1, width * 0.112);
  ctx.lineCap = "square";
  ctx.beginPath();
  ctx.moveTo(x + width * 0.24, y + height * 0.52);
  ctx.lineTo(x + width * 0.43, y + height * 0.72);
  ctx.lineTo(x + width * 0.78, y + height * 0.25);
  ctx.stroke();
  ctx.restore();
}

/**
 * 矩形の外周だけを現在の `fillStyle` で塗る（枠線の描画）。
 * `strokeRect` は `strokeStyle` / `lineWidth` の状態を持ち込むため、4 辺を塗りで描いて
 * 呼び出し前後の context の状態を変えない。`thickness` は出力スケールで換算済みの太さ。
 */
function strokeRectBorder(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  thickness: number
): void {
  if (w <= 0 || h <= 0 || thickness <= 0) return;
  const t = Math.min(thickness, w, h);
  ctx.fillRect(x, y, w, t);
  ctx.fillRect(x, y + h - t, w, t);
  ctx.fillRect(x, y, t, h);
  ctx.fillRect(x + w - t, y, t, h);
}

function assertNeverRole(role: never): never {
  throw new Error(`unexpected layout rect role: ${String(role)}`);
}

/**
 * 付箋の最終更新者名（左下の署名）を PDF へ描画する。
 * 画面表示（sticky-object.tsx）と同じレイアウト定数（左 8px / 下 4px / 右 32px / 半透明）と
 * 署名文字サイズ（付箋サイズ連動。fontBasePx はスケール前 px）に揃え、
 * 幅を超える場合は省略記号（…）で切り詰める。
 * color には背景色に応じて解決した署名色（濃い背景では白系）を渡す。
 */
function drawStickySignature(
  ctx: CanvasRenderingContext2D,
  name: string,
  w: number,
  h: number,
  scaleX: number,
  scaleY: number,
  fontBasePx: number,
  color: string
): void {
  const sigFontPx = fontBasePx * scaleY;
  ctx.font = `${sigFontPx}px sans-serif`;
  ctx.fillStyle = color;
  ctx.textAlign = "left";
  const left = 8 * scaleX;
  // めくれ三角は PDF では描画しないが、画面と同じ折り返し幅にするため右マージン 32px を確保する。
  const right = 32 * scaleX;
  const maxWidth = Math.max(0, w - left - right);
  const text = truncateToWidth(ctx, name, maxWidth);
  if (text.length === 0) return;
  // bottom:4px 相当。alphabetic baseline で下端から少し上に置き、descender が枠外へ出ないようにする。
  ctx.fillText(text, left, h - 5 * scaleY);
}

/**
 * 現在の ctx.font 設定のもとで、テキストが maxWidth に収まるよう末尾を省略記号（…）で切り詰める。
 * 呼び出し前に ctx.font を設定しておくこと。
 */
function truncateToWidth(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (maxWidth <= 0) return "";
  if (ctx.measureText(text).width <= maxWidth) return text;
  const ellipsis = "…";
  let result = text;
  while (result.length > 0 && ctx.measureText(result + ellipsis).width > maxWidth) {
    result = result.slice(0, -1);
  }
  return result.length > 0 ? result + ellipsis : ellipsis;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
